import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Filter, 
  Upload, 
  Download, 
  LayoutGrid, 
  List, 
  DollarSign, 
  TrendingUp,
  Users,
  Target,
  X,
  MapPin,
  Building2,
  User,
  Mail,
  Phone,
  Calendar,
  FileText,
  Edit,
  Trash2,
  Check
} from 'lucide-react';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { DndContext, closestCorners, PointerSensor, useSensor, useSensors, DragOverlay, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Lead stages configuration
const STAGES = [
  { id: 'New', label: 'New', color: 'bg-slate-100 text-slate-800' },
  { id: 'Contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-800' },
  { id: 'Qualified', label: 'Qualified', color: 'bg-purple-100 text-purple-800' },
  { id: 'Proposal Sent', label: 'Proposal Sent', color: 'bg-yellow-100 text-yellow-800' },
  { id: 'Negotiation', label: 'Negotiation', color: 'bg-orange-100 text-orange-800' },
  { id: 'Won', label: 'Won', color: 'bg-green-100 text-green-800' }
];

// Sortable Lead Card Component for Kanban
const SortableLeadCard = ({ lead, onView }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lead.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Card 
        className="mb-3 cursor-move hover:shadow-md transition-all duration-200 border-l-4 border-l-[#247C75] bg-white group"
        onClick={(e) => {
          // Only open drawer if not dragging
          if (!isDragging) {
            onView(lead);
          }
        }}
      >
        <CardContent className="p-4">
          <h4 className="font-semibold text-sm text-slate-900 mb-2 line-clamp-2">
            {lead.potential_project_name}
          </h4>
          
          {lead.company_name && (
            <div className="flex items-center gap-1.5 text-xs text-slate-600 mb-2">
              <Building2 className="h-3 w-3" />
              <span className="truncate">{lead.company_name}</span>
            </div>
          )}
          
          <div className="flex items-center justify-between mt-3">
            {lead.est_value && (
              <div className="flex items-center gap-1 text-sm font-semibold text-[#247C75]">
                <DollarSign className="h-4 w-4" />
                <span>${lead.est_value.toLocaleString()}</span>
              </div>
            )}
            {lead.owner && (
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <User className="h-3 w-3" />
                <span className="truncate">{lead.owner}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Droppable Column Component
const DroppableColumn = ({ stage, leads, onView }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: stage.id,
  });

  return (
    <div ref={setNodeRef}>
      <Card className={`bg-white shadow-sm ${isOver ? 'ring-2 ring-[#247C75]' : ''}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-700">
              {stage.label}
            </CardTitle>
            <Badge variant="secondary" className="text-xs">
              {leads?.length || 0}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <SortableContext
            id={stage.id}
            items={leads?.map(l => l.id) || []}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2 min-h-[400px]">
              {leads && leads.length > 0 ? (
                leads.map((lead) => (
                  <SortableLeadCard
                    key={lead.id}
                    lead={lead}
                    onView={onView}
                  />
                ))
              ) : (
                <div className="text-center py-8 text-slate-400 text-sm">
                  No leads
                </div>
              )}
            </div>
          </SortableContext>
        </CardContent>
      </Card>
    </div>
  );
};

// Lead Drawer Component
const LeadDrawer = ({ lead, isOpen, onClose, onUpdate }) => {
  const navigate = useNavigate();
  const [leadDetails, setLeadDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingStage, setEditingStage] = useState(false);
  const [selectedStage, setSelectedStage] = useState('');
  const [editingProjectName, setEditingProjectName] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [editingEstValue, setEditingEstValue] = useState(false);
  const [estValue, setEstValue] = useState('');
  const [estimate, setEstimate] = useState(null);

  useEffect(() => {
    if (lead && isOpen) {
      fetchLeadDetails();
    }
  }, [lead, isOpen]);

  const fetchLeadDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/leads/${lead.id}`);
      setLeadDetails(response.data);
      // Set initial stage
      const status = response.data.status;
      setSelectedStage((!status || status === response.data.id) ? 'New' : status);
      // Set initial project name
      setProjectName(response.data.potential_project_name || '');
      // Set initial est_value
      setEstValue(response.data.est_value || '');
      
      // Check if estimate exists for this lead
      try {
        const estimateRes = await api.get(`/estimates?lead_id=${lead.id}`);
        setEstimate(estimateRes.data || null);
      } catch (err) {
        setEstimate(null);
      }
    } catch (error) {
      console.error('Error fetching lead details:', error);
      toast.error('Failed to load lead details');
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      await api.put(`/leads/${lead.id}`, { status: newStage });
      toast.success('Stage updated successfully');
      setSelectedStage(newStage);
      setLeadDetails({ ...leadDetails, status: newStage });
      setEditingStage(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating stage:', error);
      toast.error('Failed to update stage');
    }
  };

  const handleProjectNameChange = async () => {
    if (!projectName.trim()) {
      toast.error('Project name cannot be empty');
      return;
    }
    try {
      await api.put(`/leads/${lead.id}`, { potential_project_name: projectName });
      toast.success('Project name updated successfully');
      setLeadDetails({ ...leadDetails, potential_project_name: projectName });
      setEditingProjectName(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating project name:', error);
      toast.error('Failed to update project name');
    }
  };

  const handleEstValueChange = async () => {
    if (!estValue || parseFloat(estValue) < 0) {
      toast.error('Please enter a valid estimated value');
      return;
    }
    try {
      await api.put(`/leads/${lead.id}`, { est_value: parseFloat(estValue) });
      toast.success('Estimated value updated successfully');
      setLeadDetails({ ...leadDetails, est_value: parseFloat(estValue) });
      setEditingEstValue(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating estimated value:', error);
      toast.error('Failed to update estimated value');
    }
  };

  const handleCreateProposal = async () => {
    // Navigate to proposals page with lead data pre-filled
    const proposalData = {
      prepared_for_name: leadDetails.contact_name || leadDetails.company_name || 'Client',
      prepared_for_company: leadDetails.company_name || '',
      project_property: leadDetails.potential_project_name,
      address: leadDetails.location || '',
      // Estimate line item from lead value
      line_items: leadDetails.est_value ? [{
        description: leadDetails.potential_project_name || 'Cleaning Services',
        quantity_area: leadDetails.est_sqft ? `${leadDetails.est_sqft} sq ft` : leadDetails.est_units ? `${leadDetails.est_units} units` : '1',
        unit_price: leadDetails.est_value,
        total: leadDetails.est_value
      }] : [],
      lead_id: lead.id, // Link back to lead
      estimate_date: new Date().toISOString().split('T')[0],
    };

    // Store in sessionStorage and navigate
    sessionStorage.setItem('proposalFromLead', JSON.stringify(proposalData));
    window.location.href = '/proposals?fromLead=true';
    
    toast.success('Navigating to create proposal from lead...');
    onClose();
  };

  const handleDeleteLead = async () => {
    if (!window.confirm(`Are you sure you want to delete the lead "${leadDetails?.potential_project_name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success('Lead deleted successfully');
      onClose();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Failed to delete lead');
    }
  };

  const handleCreateEstimate = () => {
    navigate(`/estimates/new?lead_id=${lead.id}`);
    onClose();
  };

  if (!isOpen || !lead) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-full md:w-[480px] bg-white shadow-2xl z-50 overflow-y-auto border-l border-slate-200">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-slate-200 p-6 z-10">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {editingProjectName ? (
              <div className="flex items-center gap-2 mb-2">
                <Input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  className="text-xl font-bold"
                  placeholder="Project Name"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleProjectNameChange();
                    } else if (e.key === 'Escape') {
                      setProjectName(leadDetails?.potential_project_name || '');
                      setEditingProjectName(false);
                    }
                  }}
                  autoFocus
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleProjectNameChange}
                  className="h-8"
                >
                  <Check className="h-4 w-4 text-green-600" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setProjectName(leadDetails?.potential_project_name || '');
                    setEditingProjectName(false);
                  }}
                  className="h-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <h2 
                className="text-xl font-bold text-slate-900 mb-2 cursor-pointer hover:text-[#247C75] transition-colors flex items-center gap-2 group"
                onClick={() => setEditingProjectName(true)}
              >
                {leadDetails?.potential_project_name || lead.potential_project_name}
                <Edit className="h-4 w-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </h2>
            )}
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-600">Stage:</Label>
              {editingStage ? (
                <div className="flex items-center gap-2">
                  <Select value={selectedStage} onValueChange={handleStageChange}>
                    <SelectTrigger className="w-[180px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STAGES.map((stage) => (
                        <SelectItem key={stage.id} value={stage.id}>
                          {stage.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingStage(false)}
                    className="h-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div 
                  className="flex items-center gap-2 cursor-pointer group"
                  onClick={() => setEditingStage(true)}
                >
                  {(() => {
                    const stage = STAGES.find(s => s.id === selectedStage) || STAGES[0];
                    return (
                      <Badge className={`${stage.color} group-hover:opacity-80 transition-opacity`}>
                        {stage.label}
                      </Badge>
                    );
                  })()}
                  <Edit className="h-3.5 w-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              )}
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={onClose}
            className="hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#247C75]"></div>
          </div>
        ) : leadDetails ? (
          <div className="space-y-6">
            {/* Value & Location */}
            <div className="grid grid-cols-2 gap-4">
              {(leadDetails.est_value || editingEstValue) && (
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-xs text-green-600 font-medium mb-1">Estimated Value</div>
                  {editingEstValue ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={estValue}
                        onChange={(e) => setEstValue(e.target.value)}
                        className="text-lg font-bold h-10"
                        placeholder="0.00"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleEstValueChange();
                          } else if (e.key === 'Escape') {
                            setEstValue(leadDetails?.est_value || '');
                            setEditingEstValue(false);
                          }
                        }}
                        autoFocus
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleEstValueChange}
                        className="h-8"
                      >
                        <Check className="h-4 w-4 text-green-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEstValue(leadDetails?.est_value || '');
                          setEditingEstValue(false);
                        }}
                        className="h-8"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div 
                      className="text-2xl font-bold text-green-700 cursor-pointer hover:text-green-800 transition-colors flex items-center gap-2 group"
                      onClick={() => setEditingEstValue(true)}
                    >
                      ${leadDetails.est_value ? leadDetails.est_value.toLocaleString() : '0'}
                      <Edit className="h-4 w-4 text-green-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  )}
                </div>
              )}
              {leadDetails.location && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="text-xs text-slate-600 font-medium mb-1">Location</div>
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {leadDetails.location}
                  </div>
                </div>
              )}
            </div>

            {/* Project Details */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-900 border-b pb-2">Project Details</h3>
              
              {leadDetails.est_units && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-600">Units</span>
                  <span className="text-sm font-medium text-slate-900">{leadDetails.est_units}</span>
                </div>
              )}
              
              {leadDetails.est_sqft && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-600">Square Feet</span>
                  <span className="text-sm font-medium text-slate-900">{leadDetails.est_sqft.toLocaleString()} sqft</span>
                </div>
              )}
              
              {leadDetails.source && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-600">Source</span>
                  <Badge variant="outline" className="text-xs">{leadDetails.source}</Badge>
                </div>
              )}
            </div>

            {/* Company & Contact Info */}
            {(leadDetails.company_name || leadDetails.contact_name) && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900 border-b pb-2">Contact Information</h3>
                
                {leadDetails.company_name && (
                  <div className="flex items-start gap-3 py-2">
                    <Building2 className="h-4 w-4 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-600">Company</div>
                      <div className="text-sm font-medium text-slate-900">{leadDetails.company_name}</div>
                    </div>
                  </div>
                )}
                
                {leadDetails.contact_name && (
                  <div className="flex items-start gap-3 py-2">
                    <User className="h-4 w-4 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-600">Contact</div>
                      <div className="text-sm font-medium text-slate-900">{leadDetails.contact_name}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            {leadDetails.notes && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-slate-900 border-b pb-2">Notes</h3>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{leadDetails.notes}</p>
              </div>
            )}

            {/* Timestamps */}
            <div className="space-y-2 pt-4 border-t">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Created</span>
                <span>{new Date(leadDetails.created_at).toLocaleDateString()}</span>
              </div>
              {leadDetails.updated_at && (
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>Last Updated</span>
                  <span>{new Date(leadDetails.updated_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* Footer Actions */}
      <div className="sticky bottom-0 bg-white border-t border-slate-200 p-6 space-y-3">
        <div className="flex gap-3">
          <Button 
            className="flex-1 bg-[#247C75] hover:bg-[#1d6259]"
            onClick={() => {
              // Toggle stage editing
              setEditingStage(!editingStage);
            }}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Stage
          </Button>
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={handleCreateEstimate}
          >
            <DollarSign className="h-4 w-4 mr-2" />
            {estimate ? 'View Estimate' : 'Create Estimate'}
          </Button>
        </div>
        <Button 
          variant="outline" 
          className="w-full"
          onClick={handleCreateProposal}
        >
          <FileText className="h-4 w-4 mr-2" />
          {estimate ? `Create Proposal (Estimate Available)` : 'Create Proposal'}
        </Button>
        <Button 
          variant="outline" 
          className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
          onClick={handleDeleteLead}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete Lead
        </Button>
      </div>
    </div>
  );
};

// Main Component
const LeadsPipelineNew = () => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'list'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const [formData, setFormData] = useState({
    potential_project_name: '',
    company_id: '',
    contact_id: '',
    source: 'Referral',
    location: '',
    est_units: '',
    est_sqft: '',
    est_value: '',
    notes: '',
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    fetchLeads();
    fetchCompanies();
    fetchContacts();
  }, []);

  useEffect(() => {
    filterLeads();
  }, [leads, searchTerm]);

  const fetchLeads = async () => {
    try {
      const response = await api.get('/leads');
      setLeads(response.data.filter(lead => !lead.deleted_at));
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to fetch leads');
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await api.get('/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const filterLeads = () => {
    if (!searchTerm) {
      setFilteredLeads(leads);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = leads.filter(lead => 
      lead.potential_project_name?.toLowerCase().includes(term) ||
      lead.company_name?.toLowerCase().includes(term) ||
      lead.location?.toLowerCase().includes(term) ||
      lead.owner?.toLowerCase().includes(term)
    );
    setFilteredLeads(filtered);
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    setActiveId(null);
    
    if (!over) return;
    
    const leadId = active.id;
    const newStatus = over.id;
    
    // Find the lead being dragged
    const lead = leads.find(l => l.id === leadId);
    
    if (!lead || lead.status === newStatus) {
      return; // No change needed
    }
    
    // Optimistic update
    setLeads(prevLeads => 
      prevLeads.map(l => 
        l.id === leadId ? { ...l, status: newStatus } : l
      )
    );
    
    try {
      await api.put(`/leads/${leadId}`, { status: newStatus });
      toast.success('Lead status updated');
    } catch (error) {
      console.error('Error updating lead status:', error);
      toast.error('Failed to update lead status');
      // Revert on error
      fetchLeads();
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const handleViewLead = (lead) => {
    setSelectedLead(lead);
    setDrawerOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/leads', {
        ...formData,
        est_units: formData.est_units ? parseInt(formData.est_units) : null,
        est_sqft: formData.est_sqft ? parseInt(formData.est_sqft) : null,
        est_value: formData.est_value ? parseFloat(formData.est_value) : null,
      });
      toast.success('Lead created successfully');
      setDialogOpen(false);
      fetchLeads();
      resetForm();
    } catch (error) {
      console.error('Error creating lead:', error);
      toast.error('Failed to create lead');
    }
  };

  const resetForm = () => {
    setFormData({
      potential_project_name: '',
      company_id: '',
      contact_id: '',
      source: 'Referral',
      location: '',
      est_units: '',
      est_sqft: '',
      est_value: '',
      notes: '',
    });
  };

  // Calculate metrics
  const metrics = {
    totalLeads: leads.length,
    won: leads.filter(l => l.status === 'Won').length,
    pipelineValue: leads.reduce((sum, l) => sum + (l.est_value || 0), 0),
    stagesActive: new Set(leads.map(l => l.status)).size,
  };

  // Group leads by stage for Kanban
  const leadsByStage = STAGES.reduce((acc, stage) => {
    acc[stage.id] = filteredLeads.filter(lead => {
      // Handle both old UUID-based status and new stage names
      if (!lead.status || lead.status === lead.id) {
        // If status is missing or is the lead's own ID, default to 'New'
        return stage.id === 'New';
      }
      return lead.status === stage.id;
    });
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Leads Pipeline</h1>
              <p className="text-slate-600 mt-1">Track and manage your client opportunities with a clean, organized view.</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="gap-2">
                <Upload className="h-4 w-4" />
                Import
              </Button>
              <Button variant="outline" className="gap-2">
                <Download className="h-4 w-4" />
                Export
              </Button>
              <Button 
                className="gap-2 bg-[#247C75] hover:bg-[#1d6259]"
                onClick={() => setDialogOpen(true)}
              >
                <Plus className="h-4 w-4" />
                Add Lead
              </Button>
            </div>
          </div>

          {/* Metrics Row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Leads</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{metrics.totalLeads}</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Target className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Won</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">{metrics.won}</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Pipeline Value</p>
                    <p className="text-2xl font-bold text-[#247C75] mt-1">
                      ${metrics.pipelineValue.toLocaleString()}
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-[#e8f5f4] flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-[#247C75]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Stages Active</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{metrics.stagesActive}</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Toolbar */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search leads, company, owner..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="gap-2">
                <Filter className="h-4 w-4" />
                Filters
              </Button>
              <div className="flex gap-1 border border-slate-200 rounded-lg p-1">
                <Button
                  variant={viewMode === 'kanban' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('kanban')}
                  className="gap-2"
                >
                  <LayoutGrid className="h-4 w-4" />
                  Kanban
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="gap-2"
                >
                  <List className="h-4 w-4" />
                  List
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-6">
        {viewMode === 'kanban' ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {STAGES.map((stage) => (
                <DroppableColumn
                  key={stage.id}
                  stage={stage}
                  leads={leadsByStage[stage.id]}
                  onView={handleViewLead}
                />
              ))}
            </div>
            <DragOverlay>
              {activeId ? (
                <Card className="mb-3 cursor-move shadow-lg border-l-4 border-l-[#247C75] bg-white opacity-90">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-sm text-slate-900">
                      {leads.find(l => l.id === activeId)?.potential_project_name}
                    </h4>
                  </CardContent>
                </Card>
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          <Card className="bg-white shadow-sm">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-100">
                    <TableHead className="font-semibold">Lead</TableHead>
                    <TableHead className="font-semibold">Company</TableHead>
                    <TableHead className="font-semibold">Stage</TableHead>
                    <TableHead className="font-semibold">Owner</TableHead>
                    <TableHead className="text-right font-semibold">Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.length > 0 ? (
                    filteredLeads.map((lead) => (
                      <TableRow
                        key={lead.id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleViewLead(lead)}
                      >
                        <TableCell className="font-medium">{lead.potential_project_name}</TableCell>
                        <TableCell>{lead.company_name || '-'}</TableCell>
                        <TableCell>
                          {(() => {
                            // Determine the actual stage
                            let stageName = lead.status;
                            if (!lead.status || lead.status === lead.id) {
                              stageName = 'New';
                            }
                            const stage = STAGES.find(s => s.id === stageName) || STAGES[0];
                            return (
                              <Badge className={stage.color}>
                                {stage.label}
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell>{lead.owner || '-'}</TableCell>
                        <TableCell className="text-right font-semibold text-[#247C75]">
                          {lead.est_value ? `$${lead.est_value.toLocaleString()}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                        No leads found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Footer Helper */}
        <div className="mt-6 text-center text-sm text-slate-500">
          {viewMode === 'kanban' 
            ? 'Drag and drop cards between stages to update progress.'
            : 'Click a row to view or edit lead details.'}
        </div>
      </div>

      {/* Add Lead Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="potential_project_name">Project Name *</Label>
                <Input
                  id="potential_project_name"
                  value={formData.potential_project_name}
                  onChange={(e) => setFormData({ ...formData, potential_project_name: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="company_id">Company</Label>
                <Select
                  value={formData.company_id}
                  onValueChange={(value) => setFormData({ ...formData, company_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="contact_id">Contact</Label>
                <Select
                  value={formData.contact_id}
                  onValueChange={(value) => setFormData({ ...formData, contact_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select contact" />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.first_name} {contact.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="source">Source *</Label>
                <Select
                  value={formData.source}
                  onValueChange={(value) => setFormData({ ...formData, source: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Referral">Referral</SelectItem>
                    <SelectItem value="ColdEmail">Cold Email</SelectItem>
                    <SelectItem value="Website">Website</SelectItem>
                    <SelectItem value="WalkIn">Walk-In</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="est_units">Est. Units</Label>
                <Input
                  id="est_units"
                  type="number"
                  value={formData.est_units}
                  onChange={(e) => setFormData({ ...formData, est_units: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="est_sqft">Est. Square Feet</Label>
                <Input
                  id="est_sqft"
                  type="number"
                  value={formData.est_sqft}
                  onChange={(e) => setFormData({ ...formData, est_sqft: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="est_value">Est. Value ($)</Label>
                <Input
                  id="est_value"
                  type="number"
                  step="0.01"
                  value={formData.est_value}
                  onChange={(e) => setFormData({ ...formData, est_value: e.target.value })}
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#247C75] hover:bg-[#1d6259]">
                Create Lead
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lead Drawer */}
      <LeadDrawer
        lead={selectedLead}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onUpdate={fetchLeads}
      />

      {/* Overlay for drawer */}
      {drawerOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-30 z-40"
          onClick={() => setDrawerOpen(false)}
        />
      )}
    </div>
  );
};

export default LeadsPipelineNew;
